import { endpoints } from '@api'
import { useExperienceManager } from '@hooks'
import React, { createContext, useContext, useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  getSessionControllerGetSessionRecapQueryKey,
  useSessionControllerGetTelehealthMeetingToken
} from '~/clinician-api'
import type { TelehealthMeetingTokenDto } from '~/clinician-api/models'
import type { AdmitWaitingParticipantDialogProps } from '../components/admit-particiapnt-dialog'

type ClinicianTelehealthSessionContextType = {
  sessionId: string
  patientId: string
  isEndSessionModalOpen: boolean
  setIsEndSessionModalOpen: (isEndSessionModalOpen: boolean) => void
  patient: any
  isLoading: boolean
  admitParticipantDialogProps: AdmitWaitingParticipantDialogProps
  setAdmitParticipantDialogProps: (
    props: AdmitWaitingParticipantDialogProps
  ) => void
  markSessionStartedRecording: () => void
  showWaitingRoomPopover: boolean
  setShowWaitingRoomPopover: (show: boolean) => void
  recordingStartedAt: string | undefined
  setRecordingStartedAt: (recordingStartedAt: string) => void
  activeSidebarTab: SideBarTab
  toggleSidebar: (tab: SideBarTab) => void
  isSidebarVisible: boolean
  meetingToken: TelehealthMeetingTokenDto | undefined
}

type SideBarTab = 'assist' | 'participants' | 'none'

export const ClinicianTelehealthSessionContext = createContext<
  ClinicianTelehealthSessionContextType | undefined
>(undefined)

export const ClinicianTelehealthSessionProvider = ({
  children,
  sessionId,
  patientId
}: {
  children: React.ReactNode
  sessionId: string
  patientId: string
}) => {
  const queryClient = useQueryClient()
  const { showAssist } = useExperienceManager()

  const [isSidebarVisible, setIsSidebarVisible] = useState(false)
  const [activeSidebarTab, setActiveSidebarTab] = useState<SideBarTab>(
    showAssist ? 'assist' : 'none'
  )

  const [showWaitingRoomPopover, setShowWaitingRoomPopover] = useState(false)
  const [isEndSessionModalOpen, setIsEndSessionModalOpen] = useState(false)
  const [admitParticipantDialogProps, setAdmitParticipantDialogProps] =
    useState<AdmitWaitingParticipantDialogProps>({
      isOpen: false,
      onAdmit: undefined,
      onClose: undefined,
      requestingParticipantName: undefined
    })

  const [recordingStartedAt, setRecordingStartedAt] = useState<
    string | undefined
  >(undefined)

  useEffect(() => {
    if (activeSidebarTab === 'none') {
      setIsSidebarVisible(false)
    } else {
      setIsSidebarVisible(true)
    }
  }, [activeSidebarTab])

  const { data: patient, isLoading: isClientLoading } = useQuery(
    [endpoints.getClinicianUserAccount.getCacheId(), patientId],
    () => endpoints.getClinicianUserAccount.request({ id: patientId }),
    { enabled: !!patientId }
  )

  const { mutateAsync: mutateStartRecording } = useMutation(
    endpoints.startAudioCapture.request,
    {
      onSuccess: () => {
        if (sessionId) {
          queryClient.invalidateQueries(
            getSessionControllerGetSessionRecapQueryKey(sessionId as string)
          )
        }
      }
    }
  )

  const { data: meetingToken, isLoading: isLoadingMeetingToken } =
    useSessionControllerGetTelehealthMeetingToken(sessionId, {
      query: {
        enabled: !!sessionId
      }
    })

  const markSessionStartedRecording = async () => {
    await mutateStartRecording({
      sessionId,
      isTelehealth: true
    })
  }

  const toggleSidebar = (tab: string) => {
    if (tab === activeSidebarTab || tab === 'none') {
      setActiveSidebarTab('none')
    } else {
      setActiveSidebarTab(tab as 'assist' | 'participants')
    }
  }

  const isLoading = isClientLoading || isLoadingMeetingToken

  return (
    <ClinicianTelehealthSessionContext.Provider
      value={{
        isLoading,
        patient,
        isEndSessionModalOpen,
        setIsEndSessionModalOpen,
        sessionId,
        patientId,
        admitParticipantDialogProps,
        setAdmitParticipantDialogProps,
        markSessionStartedRecording,
        showWaitingRoomPopover,
        setShowWaitingRoomPopover,
        recordingStartedAt,
        setRecordingStartedAt,
        activeSidebarTab,
        toggleSidebar,
        isSidebarVisible,
        meetingToken
      }}
    >
      {children}
    </ClinicianTelehealthSessionContext.Provider>
  )
}

export const useClinicianTelehealthSession = () => {
  const context = useContext(ClinicianTelehealthSessionContext)
  if (!context) {
    throw new Error(
      'useClinicianTelehealthSession must be used within a ClinicianTelehealthSessionProvider'
    )
  }
  return context
}
